/**
 * Member Activation tab of the Admin User Editor (AR-327) — the onboarding
 * tie-in: where the member is in activation, the ordered step timeline, and
 * the documents collected along the way.
 *
 * Per-step timestamps and document states come from the activation feed, which
 * this page does not have yet. The steps and documents are the real onboarding
 * set, rendered in their `not-started` / `Pending` resting state, with a legend
 * so every designed state is visible for review.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { DeferredAction, PendingIntegration, RecordCard, StatePill } from './recordPrimitives';
import { displayValue } from '@/lib/adminUserRecordApi';

export type StepState = 'completed' | 'in-progress' | 'not-started' | 'skipped';

const ACTIVATION_STEPS: { label: string; state: StepState }[] = [
  { label: 'Complete Membership Purchase', state: 'not-started' },
  { label: 'Verify Your Identity', state: 'not-started' },
  { label: 'Background Check Authorization', state: 'not-started' },
  { label: 'Banking + Direct Deposit', state: 'not-started' },
  { label: 'Build Your Brand Trifecta', state: 'not-started' },
  { label: 'Set Up Your Public CTAs', state: 'not-started' },
];

const DOCUMENTS = [
  'Background Check Authorization',
  'Performance Guaranty + Member Agreement',
  'W-9',
  'Banking Authorization (ACH)',
  'IRS EIN Letter',
];

function StepDot({ state }: { state: StepState }) {
  if (state === 'completed' || state === 'skipped') {
    return (
      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white',
          state === 'completed' ? 'bg-emerald-600' : 'bg-slate-400',
        )}
      >
        <Check className="h-3 w-3" aria-hidden="true" />
      </span>
    );
  }
  if (state === 'in-progress') {
    return <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-orange-500" />;
  }
  return (
    <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-slate-300 dark:border-gray-600" />
  );
}

const STEP_STATE_LABEL: Record<StepState, string> = {
  completed: 'Completed',
  'in-progress': 'In progress',
  'not-started': 'Not started',
  skipped: 'Skipped',
};

function SummaryStat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className={cn('mt-1 text-base font-semibold text-slate-900 dark:text-slate-100', tone)}>
        {value}
      </p>
    </div>
  );
}

export function MemberActivationTab({
  onboardingStatus,
  routeName,
  pathwayName,
}: {
  onboardingStatus: string | null;
  routeName: string | null;
  pathwayName: string | null;
}) {
  const [moveStep, setMoveStep] = useState<{ label: string; direction: 'forward' | 'back' } | null>(
    null,
  );

  return (
    <div className="flex flex-col gap-5">
      <RecordCard title="Activation Summary">
        <div className="flex flex-wrap gap-8">
          <SummaryStat label="Current Step" value={displayValue(null)} />
          <SummaryStat label="Time in Module" value={displayValue(null)} />
          <SummaryStat
            label="Onboarding Status"
            value={displayValue(onboardingStatus)}
            tone={onboardingStatus === 'active' ? 'text-emerald-600 dark:text-emerald-400' : undefined}
          />
          <SummaryStat label="Route" value={displayValue(routeName)} />
          <SummaryStat label="Pathway" value={displayValue(pathwayName)} />
        </div>
      </RecordCard>

      <RecordCard
        title="Activation Timeline"
        description="Admins can move a member forward or back a requirement step."
      >
        <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-600 dark:bg-gray-900 dark:text-slate-400">
          <span className="font-semibold uppercase tracking-wide">States</span>
          {(Object.keys(STEP_STATE_LABEL) as StepState[]).map((state) => (
            <span key={state} className="flex items-center gap-2">
              <StepDot state={state} />
              {STEP_STATE_LABEL[state]}
            </span>
          ))}
        </div>

        <ol className="flex flex-col">
          {ACTIVATION_STEPS.map((step) => (
            <li
              key={step.label}
              className="flex flex-wrap items-center gap-3 border-b border-slate-100 py-3 last:border-b-0 dark:border-gray-700"
            >
              <StepDot state={step.state} />
              <div className="min-w-[200px] flex-1">
                <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                  {step.label}
                </p>
                <p className="text-xs text-slate-400">{STEP_STATE_LABEL[step.state]}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  aria-label={`Move ${step.label} back`}
                  onClick={() => setMoveStep({ label: step.label, direction: 'back' })}
                >
                  <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  aria-label={`Advance ${step.label}`}
                  onClick={() => setMoveStep({ label: step.label, direction: 'forward' })}
                >
                  <ChevronsRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-4">
          <PendingIntegration source="the member activation feed" />
        </div>
      </RecordCard>

      <RecordCard
        title="Documents + Storage"
        description="Full record set lives on the Account tab — shown here for onboarding context."
      >
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-600 dark:bg-gray-900 dark:text-slate-400">
          <span className="font-semibold uppercase tracking-wide">States</span>
          <StatePill tone="positive">Signed</StatePill>
          <StatePill tone="neutral">Uploaded</StatePill>
          <StatePill tone="warning">Pending</StatePill>
        </div>

        <ul className="flex flex-col">
          {DOCUMENTS.map((name) => (
            <li
              key={name}
              className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 py-3 last:border-b-0 dark:border-gray-700"
            >
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Not yet submitted</p>
              </div>
              <div className="flex items-center gap-3">
                <StatePill tone="warning">Pending</StatePill>
                <Button size="sm" variant="outline" disabled>
                  View
                </Button>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-4">
          <PendingIntegration source="the onboarding document store" />
        </div>
      </RecordCard>

      <DeferredAction
        open={moveStep !== null}
        onOpenChange={(open) => !open && setMoveStep(null)}
        title={
          moveStep
            ? `Move "${moveStep.label}" ${moveStep.direction === 'forward' ? 'forward' : 'back'}`
            : ''
        }
        description="Overrides the member's activation progress and re-sends the step's notification."
        confirmLabel={moveStep?.direction === 'forward' ? 'Advance step' : 'Send back'}
        destructive={moveStep?.direction === 'back'}
        ticketNote="Activation step overrides are a companion backend ticket."
      />
    </div>
  );
}
